import {useQueries, useQuery} from '@tanstack/react-query';
import {AxiosResponse} from 'axios';
import type {Geometry} from 'geojson';
import urls from './urls.js';
import {DURATION_MS, getQueryKeys} from './util.js';
import {request} from './index.js';
import {Collection, Layer} from '@src/types/configurations.ts';

export type SearchQuery = {
  bbox?: number[];
  datetime: string;
  collections: string[];
  limit?: number;
  distinct?: string;
};

type FeatureAsset = {
  href: string;
  title?: string;
  description?: string;
  type?: string;
  roles?: string[];
};

type FeatureLink = {
  href: string;
  rel: string;
  type?: string;
  title?: string;
  method?: 'GET' | 'POST';
  headers?: Record<string, string>;
  body?: object;
  merge?: boolean;
};
type SearchResultFeature = {
  id: string;
  type: 'Feature';
  stac_version: string;
  stac_extensions: string[];
  bbox:
    | [number, number, number, number]
    | [number, number, number, number, number]
    | [number, number, number, number, number, number];
  geometry: Geometry;
  links: FeatureLink[];
  assets: Record<string, FeatureAsset>;
  properties: {
    datetime: string | null;
  } & Record<string, unknown>;
};

type SearchResultContext = {
  limit?: number;
  next?: number;
  returned: number;
};

export type CatalogSearchResults<FeatureDistinctType> = {
  type: 'FeatureCollection';
  context: SearchResultContext;
  links: FeatureLink[];
  features: FeatureDistinctType[];
};

export const getCatalogSearch = <
  T extends string | SearchResultFeature = SearchResultFeature,
>(
  query: SearchQuery,
): Promise<CatalogSearchResults<T>> => {
  return request
    .post(`${urls.catalog}/search`, query, {
      headers: {Accept: 'application/geo+json'},
    })
    .then((res: AxiosResponse) => res.data);
};

const getCollection = (collectionId: string) => {
  return request
    .get<Layer>(`${urls.catalog}/collections/${collectionId}`)
    .then((res: AxiosResponse<Layer>) => res.data);
};

const catalogQueryKeys = getQueryKeys('catalog');

export const useGetCatalogSearch = <
  T extends string | SearchResultFeature = SearchResultFeature,
>(
  query?: SearchQuery,
  options: {enabled?: boolean} = {enabled: true},
) => {
  return useQuery({
    queryKey: catalogQueryKeys.list({query}),
    queryFn: () => getCatalogSearch<T>(query!),
    staleTime: DURATION_MS.HOUR,
    enabled:
      !!query?.bbox &&
      !!query?.datetime &&
      !!query?.collections?.length &&
      options.enabled,
  });
};

export const useGetCollection = (collectionId: string) => {
  return useQuery<Collection>({
    queryKey: catalogQueryKeys.detail(collectionId),
    queryFn: () => getCollection(collectionId),
    enabled: !!collectionId,
    staleTime: DURATION_MS.INFINITY,
  });
};

export const useGetCollections = (collectionIds: string[]) => {
  return useQueries({
    queries: collectionIds.map(collectionId => ({
      queryKey: catalogQueryKeys.detail(collectionId),
      queryFn: () => getCollection(collectionId),
      enabled: !!collectionId,
      staleTime: DURATION_MS.INFINITY,
    })),
  });
};
